/**
 * CRM Client Compliance Page - BAS tracker, tax return progress, compliance status.
 */

import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  CheckCircle, Clock, AlertCircle, FileText, Shield,
  Loader2, ArrowRight, Calendar,
} from 'lucide-react';
import { crmPortalService } from '@/services/api/crm-portal';
import { cn } from '@/lib/utils';

export function CRMCompliancePage() {
  const { data, isLoading } = useQuery({
    queryKey: ['crm-compliance'],
    queryFn: () => crmPortalService.getComplianceStatus(),
  });

  if (isLoading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }

  const compliance = data?.compliance;

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Compliance Status</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Track your BAS, tax returns, and compliance obligations
        </p>
      </div>

      {/* Traffic Light Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatusCard
          title="BAS"
          status={compliance?.bas?.status || 'unknown'}
          detail={compliance?.bas?.detail || 'Contact your accountant'}
          icon={FileText}
        />
        <StatusCard
          title="Tax Return"
          status={compliance?.taxReturn?.status || 'unknown'}
          detail={compliance?.taxReturn?.detail || 'Contact your accountant'}
          icon={Shield}
        />
        <StatusCard
          title="Super"
          status={compliance?.super?.status || 'unknown'}
          detail={compliance?.super?.detail || 'Contact your accountant'}
          icon={CheckCircle}
        />
        <StatusCard
          title="PAYG"
          status={compliance?.payg?.status || 'unknown'}
          detail={compliance?.payg?.detail || 'Contact your accountant'}
          icon={Calendar}
        />
      </div>

      {/* Tax Return Progress */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <FileText className="h-4 w-4" /> Tax Return Progress
          </CardTitle>
        </CardHeader>
        <CardContent>
          {compliance?.taxReturnSteps ? (
            <div className="space-y-3">
              {compliance.taxReturnSteps.map((step: any, i: number) => (
                <div key={i} className="flex items-center gap-3">
                  <div className={cn(
                    'h-6 w-6 rounded-full flex items-center justify-center shrink-0 text-xs font-bold',
                    step.completed ? 'bg-green-500 text-white' :
                    step.current ? 'bg-[#5048e5] text-white' :
                    'bg-muted text-muted-foreground'
                  )}>
                    {step.completed ? <CheckCircle className="h-3.5 w-3.5" /> : i + 1}
                  </div>
                  <div className="flex-1">
                    <p className={cn('text-sm', step.completed ? 'text-muted-foreground line-through' : step.current ? 'font-medium' : '')}>
                      {step.name}
                    </p>
                  </div>
                  {step.current && (
                    <Badge className="bg-[#5048e5] text-[10px]">In Progress</Badge>
                  )}
                  {step.completed && (
                    <Badge variant="outline" className="text-green-600 text-[10px]">Done</Badge>
                  )}
                </div>
              ))}
              <div className="mt-2">
                <Progress value={compliance.taxReturnProgress || 0} className="h-2" />
                <p className="text-xs text-muted-foreground mt-1">{compliance.taxReturnProgress || 0}% complete</p>
              </div>
            </div>
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              <FileText className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Tax return progress will appear here when your accountant starts working on it.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* BAS History */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <FileText className="h-4 w-4" /> BAS History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {compliance?.basHistory && compliance.basHistory.length > 0 ? (
            <div className="space-y-2">
              {compliance.basHistory.map((bas: any, i: number) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/30">
                  <div>
                    <p className="text-sm font-medium">{bas.period}</p>
                    <p className="text-xs text-muted-foreground">{bas.dueDate}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {bas.amount !== undefined && (
                      <span className={cn('text-sm font-medium', bas.amount >= 0 ? '' : 'text-green-600')}>
                        {bas.amount >= 0 ? `$${bas.amount.toLocaleString()} payable` : `$${Math.abs(bas.amount).toLocaleString()} refund`}
                      </span>
                    )}
                    <Badge className={cn('text-[10px]',
                      bas.status === 'lodged' ? 'bg-green-100 text-green-700' :
                      bas.status === 'overdue' ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
                    )}>
                      {bas.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              <p className="text-sm">BAS history will appear here once your accountant lodges your first BAS.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatusCard({ title, status, detail, icon: Icon }: {
  title: string; status: string; detail: string; icon: any;
}) {
  const statusConfig: Record<string, { color: string; bg: string; label: string }> = {
    green: { color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-900/30', label: 'Up to date' },
    amber: { color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900/30', label: 'Action needed' },
    red: { color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900/30', label: 'Overdue' },
    unknown: { color: 'text-muted-foreground', bg: 'bg-muted/30', label: 'Not available' },
  };

  const cfg = statusConfig[status] || statusConfig.unknown;

  return (
    <Card className={cn(cfg.bg)}>
      <CardContent className="p-4 text-center">
        <Icon className={cn('h-6 w-6 mx-auto mb-1', cfg.color)} />
        <p className="text-xs font-semibold uppercase tracking-wider mb-0.5">{title}</p>
        <p className={cn('text-xs font-medium', cfg.color)}>{cfg.label}</p>
        <p className="text-[10px] text-muted-foreground mt-1">{detail}</p>
      </CardContent>
    </Card>
  );
}
